import usePatientQueries from "@/hooks/data/usePatientQueries";
import { usePatientQueryColumns } from "@/hooks/gridColumns/usePatientQueryColumns";
import { Button, ModalProps } from "@mui/material";
import { GridEventListener } from "@mui/x-data-grid";
import { CollapsibleTable, EventDefinitionsContext, ICollapsibleTableProps, InstitutionContext, PatientContext, QueryDialog, useEventById, useFormById, useFormMetadata, useQueryById } from "@ngt/opms";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";

export interface IPatientQueryTableProps extends Omit<ICollapsibleTableProps, "columns" | "rows" | "loading"> {
    onDialogClose?: ModalProps['onClose'];
    preventDefaultDialogClose?: boolean,
    preventDefaultRowClick?: boolean;
}

export const PatientQueryTable: FC<IPatientQueryTableProps> = ({
    onDialogClose,
    preventDefaultDialogClose,
    onRowClick,
    preventDefaultRowClick,
    ...props
}) => {
    const [selectedQueryId, setSelectedQueryId] = useState<number | undefined>(undefined);
    const [selectedQueryFieldLabel, setSelectedQueryFieldLabel] = useState<string | undefined>(undefined)
        
    const {data: query, loading: queryLoading, refresh: refreshQuery} = useQueryById(selectedQueryId);
    const {data: patient} = useContext(PatientContext);
    const {data: patientQueries, loading: patientQueriesLoading, refresh: refreshPatientQueries} = usePatientQueries(patient?.id);
    const formMetadata = useFormMetadata(query?.formDefinitionId);
    const {data: form} = useFormById(formMetadata, query?.formId);
    const {data: event} = useEventById(form?.eventId);
    const {data: eventDefinitions} = useContext(EventDefinitionsContext);
    const columns = usePatientQueryColumns(patientQueries);
    const {data: institution} = useContext(InstitutionContext);

    const onRowClickCombined = useCallback<GridEventListener<"rowClick">>((params, event, details) => {
        if (!preventDefaultRowClick) {
            setSelectedQueryId(params.row.id);
            setSelectedQueryFieldLabel(params.row.fieldName)
        }

        if (onRowClick) {
            (onRowClick as any)?.(params as any, event, details);
        }
    }, [setSelectedQueryId, setSelectedQueryFieldLabel, onRowClick]);

    const onCloseCombined = useCallback(async (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
        if (onDialogClose) {
            onDialogClose(event, reason);
        }

        if (refreshPatientQueries) {
            await refreshPatientQueries();
        }

        if (!preventDefaultDialogClose) {
            setSelectedQueryId(undefined);
            setSelectedQueryFieldLabel(undefined);
        }
    }, [onDialogClose, setSelectedQueryFieldLabel, setSelectedQueryId, refreshPatientQueries]);

    const onAfterSubmit = useCallback(async () => {
        await refreshQuery();
        await refreshPatientQueries();
    }, [refreshQuery, refreshPatientQueries]);

    const eventDefinition = useMemo(() => {
        if (event?.eventDefinitionId && eventDefinitions) {
            return eventDefinitions.find(ed => ed.id == event.eventDefinitionId);
        }

        return undefined;
    }, [eventDefinitions, event?.eventDefinitionId]);
    
    const dialogTitle = useMemo(() => {
        const titleParts: string[] = [];

        if (patient?.studyNumber) {
            titleParts.push(patient.studyNumber);
        }

        if (eventDefinition?.name) {
            titleParts.push(eventDefinition.name);
        }

        if (formMetadata?.title) {
            titleParts.push(formMetadata.title);
        }

        if (selectedQueryFieldLabel) {
            titleParts.push(selectedQueryFieldLabel);
        }

        return titleParts.join(" - ");
    }, [patient?.studyNumber, eventDefinition?.name, formMetadata?.title, selectedQueryFieldLabel]);

    const additionalActionContent = useMemo(() => [
        () => (
            <Button
                variant={"contained"}
                component={Link}
                to={`/registration/${institution?.code}/${patient?.studyNumber}/${eventDefinition?.code}/${event?.repeat}/${formMetadata?.formDefinitionCode}/${form?.repeat}`}
            >
                Go to form
            </Button>
        )
    ], [institution?.code, patient?.studyNumber, eventDefinition?.code, event?.repeat, formMetadata?.formDefinitionCode, form?.repeat])

    
    return(
        <>
            <QueryDialog 
                headingText={dialogTitle}
                query={query}
                hideNew
                open={!!selectedQueryId}
                loading={queryLoading}
                onClose={onCloseCombined}
                onAfterSubmit={onAfterSubmit}
                maxWidth={"md"}
                fullWidth
                additionalActionContent={additionalActionContent}
            />
            <CollapsibleTable
                {...props as any}
                rows={patientQueries ?? []}
                columns={columns}
                loading={patientQueriesLoading}
                onRowClick={onRowClickCombined}
            />
        </>
    )
}

export default PatientQueryTable;